import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';

import { View, useWindowDimensions } from 'react-native';

import debounce from 'lodash.debounce';

import isFunction from 'utils/function';

import ScrollArrow from './components/ScrollArrow';
import styles from './styles';

type Props = {
    viewId: string;
    hasGallery?: boolean;
    onPress?: (yOffset: number) => void;
};

function ScrollArrowButtons({ viewId, hasGallery = false, onPress }: Props) {
    const scrollableView = document.getElementById(viewId);
    const scrollableHeight = useMemo(
        () => (scrollableView?.clientHeight || 0) / 3,
        [scrollableView],
    );

    const [epgScrollPosition, setEpgScrollPosition] = useState(
        scrollableView?.offsetTop ?? 0,
    );
    const [isUpButtonDisabled, setIsUpButtonDisabled] = useState(true);
    const [isDownButtonDisabled, setIsDownButtonDisabled] = useState(false);

    const { height } = useWindowDimensions();

    const onUpArrowPress = useCallback(() => {
        if (isFunction(onPress) && scrollableView) {
            const epgScrollableHeight = epgScrollPosition - scrollableHeight;

            onPress(epgScrollableHeight);
            setEpgScrollPosition(epgScrollableHeight);
        } else {
            scrollableView?.scrollBy({
                top: -scrollableHeight * 1.7,
                behavior: 'smooth',
            });
        }
    }, [
        scrollableView,
        epgScrollPosition,
        scrollableHeight,
        onPress,
        hasGallery,
    ]);

    const onDownArrowPress = useCallback(() => {
        if (isFunction(onPress) && scrollableView) {
            const epgScrollableHeight = epgScrollPosition + scrollableHeight;

            onPress(epgScrollableHeight);
            setEpgScrollPosition(epgScrollableHeight);
        } else {
            scrollableView?.scrollBy({
                top:
                    scrollableHeight *
                    (hasGallery && isUpButtonDisabled ? 1.8 : 1.5),
                behavior: 'smooth',
            });
        }
    }, [
        scrollableView,
        scrollableHeight,
        epgScrollPosition,
        onPress,
        hasGallery,
        isUpButtonDisabled,
    ]);

    const handleScrollEvent = debounce(
        (event: Event) => {
            const { scrollTop, offsetHeight, scrollHeight } =
                event.target as HTMLElement;

            if (isFunction(onPress)) {
                setEpgScrollPosition(scrollTop);
            }

            setIsUpButtonDisabled(scrollTop === 0);
            setIsDownButtonDisabled(
                Math.ceil(scrollTop + offsetHeight) >= scrollHeight - 1,
            );
        },
        100,
        { leading: true },
    );

    useLayoutEffect(() => {
        scrollableView?.addEventListener('scroll', handleScrollEvent);

        return () => {
            scrollableView?.removeEventListener('scroll', handleScrollEvent);
        };
    }, [scrollableView, handleScrollEvent]);

    return (
        <View style={[styles.container, { top: (height - 34) / 2 }]}>
            <ScrollArrow
                onPress={onUpArrowPress}
                disabled={isUpButtonDisabled}
                isUpArrow
            />
            <ScrollArrow
                onPress={onDownArrowPress}
                disabled={isDownButtonDisabled}
            />
        </View>
    );
}

export default ScrollArrowButtons;
